import React, { useState } from 'react';

import { Grid, makeStyles, Theme } from '@material-ui/core';
import clsx from 'clsx';
import { Translate, TranslateFunction } from 'react-localize-redux';

import { Box, Button, ExternalLink, Typography } from '@fd/ui/atoms';
import { FormSection } from '@fd/ui/molecules';

import { ReactComponent as NoteIcon } from '../../../../../assets/images/StickyNote.svg';
import { UnpublishConfirmationDialog } from '../UnpublishConfirmationDialog';

const useStyles = makeStyles((theme: Theme) => ({
  appInfoRow: {
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(4),
  },
  box: {
    marginTop: theme.spacing(3),
  },
  button: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(5),
  },
  container: {
    marginTop: theme.spacing(4),
  },
  dangerButton: {
    color: '#FF2346',
  },
  linkText: {
    color: theme.palette.primary.main,
  },
  notesContainer: {
    backgroundColor: '#eaf2ff',
    borderRadius: '8px',
    display: 'flex',
    flexDirection: 'row',
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
  },
  notesIcon: {
    color: theme.palette.grey[700],
    marginRight: theme.spacing(3),
  },
  notesText: {
    whiteSpace: 'pre-line',
  },
}));

export type AppInformationProps = {
  appBundleId: string;
  isLoading?: boolean;
  isPublished?: boolean;
  lastUpdated: string;
  releaseNotes?: string;
  storeLink: string;
  translate: TranslateFunction;
};

export const AppInformation = (props: AppInformationProps) => {
  const {
    appBundleId,
    isLoading,
    isPublished,
    lastUpdated,
    storeLink,
    releaseNotes,
    translate,
  } = props;
  const [isUnpublishDialogOpen, setIsUnpublishDialogOpen] = useState<boolean>(false);
  const classes = useStyles();

  const renderPublishSection = () => {
    if (isPublished) {
      const buttonClasses = clsx(classes.button, classes.dangerButton);
      return (
        <FormSection
          isLoading={isLoading}
          sectionTitle={translate('Unpublish') as string}
          showDivider={false}
        >
          <div>
            <Typography className={classes.notesText} variant="caption">
              <Translate id="Android_unpublish_description" />
            </Typography>
          </div>
          <Button
            className={buttonClasses}
            fdKey="unpublish-button"
            onClick={() => setIsUnpublishDialogOpen(true)}
            variant="secondary"
          >
            {translate('Unpublish')} {translate('app')}
          </Button>
        </FormSection>
      );
    }
    return (
      <FormSection
        isLoading={isLoading}
        sectionTitle={translate('Publish') as string}
        showDivider={false}
      >
        <Button
          className={classes.button}
          fdKey="publish-button"
          onClick={() => {}}
          variant="primary"
        >
          {translate('Publish')} {translate('app')}
        </Button>
      </FormSection>
    );
  };

  return (
    <Box className={classes.box}>
      <UnpublishConfirmationDialog
        onCancel={() => setIsUnpublishDialogOpen(false)}
        open={isUnpublishDialogOpen}
        onConfirm={() => setIsUnpublishDialogOpen(false)}
        translate={translate}
      />
      <FormSection isLoading={isLoading} sectionTitle={translate('Playstore_link') as string}>
        <ExternalLink href={storeLink}>
          <Typography className={classes.linkText} variant="body2">
            {storeLink}
          </Typography>
        </ExternalLink>
      </FormSection>
      <FormSection isLoading={isLoading} sectionTitle={translate('App_info') as string}>
        <Typography variant="caption">
          <Translate id="App_info_description" />
        </Typography>
        <Grid container>
          <Grid className={classes.appInfoRow} item xs={6}>
            <Typography variant="body2">
              <Translate id="App_bundle_ID" />
            </Typography>
          </Grid>
          <Grid className={classes.appInfoRow} item xs={6}>
            <Typography variant="body2">{appBundleId}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2">
              <Translate id="Last_updated" />
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2">{lastUpdated}</Typography>
          </Grid>
        </Grid>
      </FormSection>
      <FormSection isLoading={isLoading} sectionTitle={translate('Release_notes') as string}>
        <div className={classes.notesContainer}>
          <NoteIcon className={classes.notesIcon} />
          <Typography className={classes.notesText} variant="body2">
            {releaseNotes}
          </Typography>
        </div>
      </FormSection>
      {renderPublishSection()}
    </Box>
  );
};
